/*!

=========================================================
* Argon Dashboard React - v1.2.1
=========================================================

* Product Page: https://www.creative-tim.com/product/argon-dashboard-react
* Copyright 2021 Creative Tim (https://www.creative-tim.com)
* Licensed under MIT (https://github.com/creativetimofficial/argon-dashboard-react/blob/master/LICENSE.md)

* Coded by Creative Tim

=========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.

*/
import Paper from "@material-ui/core/Paper";
// import { DateTime } from "luxon";
// import qs from "query-string";
import React, { useEffect, useState } from "react";
import { useQueryClient } from "react-query";
import { useHistory, useLocation } from "react-router-dom";
import {
  Alert,
  Card,
  CardFooter,
  CardHeader,
  Col,
  Container,
  Row,
  Spinner,
  Table,
  Button,
} from "reactstrap";
import CustomPagination from "../components/CustomPagination";
// import Header from "../components/Headers/Header";
import EventsListScreenHeader from "../components/Headers/EventsListScreenHeader";
// import Tooltip from "../components/Tooltip";
import { useAllEarningsCallEvent } from "../hooks/useAllEarningsCallEvent";
import { useQueryParams } from "../hooks/useQueryParams";
// import { secondsToTimeString } from "../utils/secondsToTimeString";
// import { toCustomDateTimeString } from "../utils/toCustomDateTimeString";
// import { toTitleCase } from "../utils/toTitleCase";
// import { updateQueryParams } from "../utils/updateQueryParams";
import { DateTimeFormatOptions } from "luxon";
// import { AxiosError } from "axios";
import axiosClient from "../utils/axiosClient";
import { useStartAnalysisByFileMutationEarnings } from "../hooks/useStartAnalysisByFileMutationEarnings";
import { ConfType } from "../types";
// import { useQuery } from "react-query";

const RESULTS_PER_PAGE = 10;

const Transcript: React.FC = () => {
  const query = useQueryParams();
  const [pageTable, setPageTable] = useState(() => {
    let page = +(query.get("page") || 0);
    if (isNaN(page) || page <= 0) {
      return 1;
    }
    return page;
  });
  // const [searchTerm, setSearchTerm] = useState(() => {
  //   const searchTerm = query.get("searchTerm");
  //   if (searchTerm) {
  //     return searchTerm;
  //   }
  //   return "";
  // });
  const queryClient = useQueryClient();
  const location = useLocation();
  const router = useHistory();

  const searchParams = new URLSearchParams(location.search);
  const transcriptId = searchParams.get("transcriptId");
  // console.log("transcriptId", typeof(transcriptId));

  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [analyzeError, setAnalyzeError] = useState("");
  const [analyzingRow, setAnalyzingRow] = useState<string | null>(null);
  // const [redirectedUrl, setRedirectedUrl] = useState<string | null>(null);

  const [mutation, progress] = useStartAnalysisByFileMutationEarnings((err, msg) => {
    if (err) {
      setAnalyzeError(msg ?? "Something went wrong.");
    }
    setIsAnalyzing(false);
  });

  const { isError, isLoading, data, refetch } = useAllEarningsCallEvent(
    pageTable,
    RESULTS_PER_PAGE,
    transcriptId || ""
  );

  // console.log("data in page ----", data?.data?.data);

  useEffect(() => {
    handleConfSearch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageTable]);

  const handleConfSearch = () => {
    if (isLoading) {
      return;
    }
    queryClient.removeQueries(["transcript"]);
    refetch();
  };

  function formatDate(dateString: string) {
    const options: DateTimeFormatOptions = {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "numeric",
      hour12: true,
    };
    const formattedDate = new Date(dateString).toLocaleString("en-US", options);
    return formattedDate;
  }

  const handleAnalyzeTranscript = async (
    audio: string,
    // companyId: string,
    title: string,
    transcriptId: string,
    rowId: string,
    transcript: string
  ) => {
    let audioFile = null;
    let textFile = null;
    try {
      // console.log("transcriptId----", transcriptId);
      // console.log("companyId----", companyId);
      if (audio !== "") {
        // console.log("audio----", audio);
        setIsAnalyzing(true);
        setAnalyzeError("");
        setAnalyzingRow(rowId);

        const data = await axiosClient.get<any>(
          `/earningsCall/proxy/audio?url=${audio}`
        );

        // // console.log("data----", data.data);
        let redirectedUrl: string = data.data;
        // let redirectedUrl = "https://static2.finnhub.io/file/privatedatany/audio/6674ba2b57cc481e39ff1c1a99742632f3d21f304f88b4cf603dea94afbbe684_tr.mp3?Authorization=3_20240526161742_75bd855566634a4668c33052_6478e167e3a95aad3b6916d15aed4ea00c963dd5_002_20240527161742_0006_dnld";

        // Check if the redirected URL is available
        if (redirectedUrl) {
          // Download the audio file using the redirected URL
          const response = await axiosClient.get(redirectedUrl, {
            responseType: "blob",
          });

          audioFile = new File([response.data], "audio.mp3");
          // console.log("audioFile----", audioFile);

          // Create a .txt file from the transcript string
          if (transcript) {
            const textBlob = new Blob([transcript], { type: "text/plain" });
            const textFileName = `transcript_${Date.now()}.txt`;
            textFile = new File([textBlob], textFileName);

            // console.log("textFile----", textFile);

            // Call the /analysis/start/byFile API
            await mutation.mutateAsync({
              confName: `${title}`,
              sessionName: `${transcriptId}`,
              file: audioFile,
              textFile: textFile,
              type: ConfType.financial,
            });

            // Redirect to admin/monitor after successful analysis
            router.push("/admin/monitor");
          } else {
            setAnalyzeError("Error: Unable to fetch transcript.");
          }
        } 
        else {
          setAnalyzeError("Error: Unable to fetch audio URL.");
        }
      } else {
        setAnalyzeError("Error: Audio URL doesn't exist.");
      }
    } catch (error) {
      setAnalyzeError("Error occurred while analyzing the transcript.");
      console.error("Error analyzing transcript:", error);
    } finally {
      setAnalyzingRow(null);
      // Revoke the object URL to free up memory
      if (audioFile) {
        URL.revokeObjectURL(URL.createObjectURL(audioFile));
      }
      if (textFile) {
        URL.revokeObjectURL(URL.createObjectURL(textFile));
      }
    }
  };

  // const handlePressEnter = () => {
  //   setPageTable(1);
  //   handleConfSearch();
  //   const q = qs.parse(location.search);
  //   q.searchTerm = searchTerm;
  //   if (searchTerm.trim().length === 0) {
  //     delete q.searchTerm;
  //   }
  //   // q.page = `${pageTable}`
  //   updateQueryParams(qs.stringify(q), router, location.pathname);
  // };

  const generateDownloadLink = (text: string, filename: string) => {
    const blob = new Blob([text], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    return (
      <a href={url} download={filename}>
        Download
      </a>
    );
  };

  // const truncateText = (text: string) => {
  //   const maxLength = 100;
  //   if (text.length > maxLength) {
  //     return text.substring(0, maxLength) + "...";
  //   }
  //   return text;
  // }

  let content: JSX.Element | null = null;

  if (isError) {
    content = <Alert color="danger">Something went wrong!</Alert>;
  } else if (isLoading) {
    content = <Spinner />;
  } else if (!data || data.data.length === 0) {
    content = <Alert color="warning">No data available</Alert>;
  } else {
    content = (
      <>
        <Table className="align-items-center table-flush" responsive>
          <thead className="thead-light" style={{ textAlign: "center" }}>
            <tr>
              <th scope="col">Event ID</th>
              <th scope="col">Title</th>
              <th scope="col">Event Date</th>
              <th scope="col">Audio Url</th>
              <th scope="col">Transcript</th>
              <th scope="col">Analyze</th>
            </tr>
          </thead>
          <tbody>
            {data?.data.data?.map((ex: any, i: number) => (
              <tr key={i}>
                <th scope="row" style={{ textAlign: "center" }}>
                  {ex.id}
                </th>
                <th scope="row" style={{ textAlign: "center" }}>
                  {ex.title}
                </th>
                <th scope="row" style={{ textAlign: "center" }}>
                  {formatDate(ex.time)}
                </th>
                <th scope="row" style={{ textAlign: "center" }}>
                  <a href={ex.audio} target="_blank" rel="noopener noreferrer">
                    <i className="fas fa-external-link-alt mr-1" />
                  </a>
                </th>
                {/* <th
                  scope="row"
                  style={{
                    textAlign: "center",
                    whiteSpace: "normal",
                    wordWrap: "break-word",
                  }}
                >
                  {truncateText(ex.transcript)}
                </th> */}


                { <th
                  scope="row"
                  style={{
                    textAlign: "center",
                    whiteSpace: "normal",
                    wordWrap: "break-word",
                  }}
                >
                  {generateDownloadLink(
                    ex.transcript,
                    `transcript_${ex.id}.txt`
                  )}
                </th> }

                <td style={{ textAlign: "center" }}>
                  <Button
                    onClick={() =>
                      handleAnalyzeTranscript(
                        ex.audio,
                        // ex.id,
                        ex.title,
                        ex.id,
                        ex.id,
                        ex.transcript
                      )
                    }
                    disabled={isAnalyzing || analyzingRow === ex.id}
                    style={{ backgroundColor: 'lightblue', color: 'black' }} // Inline styling
                  >
                    {analyzingRow === ex.id ? (
                      <>
                        Analyzing... ({progress}%)
                        <span
                          className="spinner-border spinner-border-sm ml-2"
                          role="status"
                          aria-hidden="true"
                        ></span>
                      </>
                    ) : (
                      "Analyze"
                    )}
                  </Button>
                  {analyzeError && (
                    <div className="text-danger mt-2">{analyzeError}</div>
                  )}
                </td>
                {/* <td>{toTitleCase(ex.status)}
                    {ex.errorMessage &&
                      <span className='ml-2'><Tooltip text={`Error: ${ex.errorMessage}`} /></span>}
                  </td> */}
              </tr>
            ))}
          </tbody>
        </Table>
        <CardFooter className="py-4">
          <nav aria-label="...">
            <CustomPagination
              activePage={pageTable}
              totalResults={data?.data?.total}
              resultsPerPage={RESULTS_PER_PAGE}
              onChange={setPageTable}
              label="Table navigation"
            />
          </nav>
        </CardFooter>
      </>
    );
  }
  return (
    <>
      <EventsListScreenHeader />
      <Container className="mt--7" fluid>
        <Row className="mt-5">
          <Col className="mb-5 mb-xl-0">
            <Card className="shadow">
              <CardHeader className="border-0">
              </CardHeader>
              <Paper className="px-4">
                <div className="mb-3">
                  <h4 style={{ display: "inline" }}>Transcript</h4>
                </div>
                {content}
              </Paper>
            </Card>
          </Col>
        </Row>
      </Container>
    </>
  );
};

export default Transcript;

